import { z } from 'zod';
import { demandLevelSchema, stockLevelSchema } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { amountSchema, fractionSchema, isoDateSchema, revisionSchema } from '../../common/schemas/money.schemas.js';
/**
 * The trade-in case of one **stock vehicle** (plan §5.9, PP §7.4).
 *
 * It is deliberately small. The `TRADE_IN`-scoped equations turn these figures into a `supportRate` and a
 * `support` amount that the Costs tab shows beside the ceiling as a *scenario* — "maximum trade-in offer
 * = ceiling + support". They **never** enter `total`, `retail` or the dashboard, and no approval hangs off
 * them in MVP: Finance has not defined the funding and allocation policy yet (PP §7.4), so the platform
 * records the inputs and shows the arithmetic without pretending the money has been committed.
 */
export const tradeInCaseSchema = z.object({
    id: uuidSchema,
    vehicleId: uuidSchema,
    newVehicleListCode: z.string().nullable(),
    newVehicleModel: z.string().nullable(),
    newVehicleProfit: amountSchema.nullable(),
    /** A fraction, like every percentage in the system (plan §11.6). */
    newVehicleMargin: fractionSchema.nullable(),
    profitSource: z.string().nullable(),
    profitSourceDate: isoDateSchema.nullable(),
    demandLevel: demandLevelSchema.nullable(),
    stockLevel: stockLevelSchema.nullable(),
    /** Finance's approved cap; absent falls back to the template's `supportCap`. */
    supportCap: amountSchema.nullable(),
    fundingDepartment: z.string().nullable(),
    lossRecoveryAmount: amountSchema.nullable(),
    lossRecoveryNote: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const getTradeInInputSchema = z.object({
    vehicleId: uuidSchema,
});
export const tradeInOutputSchema = tradeInCaseSchema.nullable();
export const upsertTradeInInputSchema = z.object({
    vehicleId: uuidSchema,
    expectedRevision: revisionSchema,
    newVehicleListCode: z.string().trim().max(80).nullish(),
    newVehicleModel: z.string().trim().max(120).nullish(),
    newVehicleProfit: amountSchema.nullish(),
    newVehicleMargin: fractionSchema.nullish(),
    profitSource: z.string().trim().max(255).nullish(),
    profitSourceDate: isoDateSchema.nullish(),
    demandLevel: demandLevelSchema.nullish(),
    stockLevel: stockLevelSchema.nullish(),
    supportCap: amountSchema.nullish(),
    fundingDepartment: z.string().trim().max(120).nullish(),
    lossRecoveryAmount: amountSchema.nullish(),
    lossRecoveryNote: z.string().trim().max(2_000).nullish(),
});
//# sourceMappingURL=trade-in.schemas.js.map